import React from 'react'
import { LuCalendar, LuClock } from "react-icons/lu";

const PaymentSchedulePreview = ({ formData, planType }) => {

  const generateSchedule = () => {
    // Validation for different plan types
    if (planType === 'pretreatment') {
      if (!formData.firstPaymentDate || !formData.noOfInstallments || !formData.frequency || !formData.totalTreatmentCost) {
        return [];
      }
    } else if (planType === 'BAL') {
      if (!formData.firstPaymentDate || !formData.noOfInstallments || !formData.frequency || !formData.totalBalance) {
        return [];
      }
    } else {
      // For subscription plans, finalPaymentDate is optional
      if (!formData.firstPaymentDate || !formData.recurringAmount || !formData.frequency) {
        return [];
      }
    }

    const start = new Date(formData.firstPaymentDate);
    let recurringAmount;
    let noOfInstallments;

    if (planType === 'pretreatment') {
      // For pre-treatment, calculate recurringAmount and use installment count
      const remainingBalance = (formData.totalTreatmentCost || 0) - (formData.downPayment || 0);
      recurringAmount = remainingBalance / (formData.noOfInstallments || 1);
      noOfInstallments = formData.noOfInstallments || 1;
    } else if (planType === 'BAL') {
      // For BAL plans, calculate recurringAmount and use installment count
      recurringAmount = (formData.totalBalance || 0) / (formData.noOfInstallments || 1);
      noOfInstallments = formData.noOfInstallments || 1;
    } else {
      // For subscription plans
      recurringAmount = formData.recurringAmount;
      if (formData.finalPaymentDate) {
        const end = new Date(formData.finalPaymentDate);
        if (start >= end) {
          return [];
        }
        // Calculate installments based on date range
        noOfInstallments = 12; // Show up to 12 payments for preview
      } else {
        // No end date - ongoing subscription
        noOfInstallments = 12; // Show 12 payments for preview
      }
    }

    const frequency = formData.frequency;

    const schedule = [];
    let currentDate = new Date(start);

    // Calculate increment based on frequency
    const getNextDate = (date, freq) => {
      const nextDate = new Date(date);
      switch (freq) {
        case 'D':
          nextDate.setDate(nextDate.getDate() + 1);
          break;
        case 'W':
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case 'BW':
          nextDate.setDate(nextDate.getDate() + 14);
          break;
        case 'M':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
        case 'Q':
          nextDate.setMonth(nextDate.getMonth() + 3);
          break;
        default:
          // Default to monthly if frequency is not recognized
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
      }
      return nextDate;
    };

    let paymentNumber = 1;

    if (planType === 'pretreatment' || planType === 'BAL') {
      // For pre-treatment, generate exact number of installments
      for (let i = 0; i < noOfInstallments && i < 12; i++) {
        schedule.push({
          paymentNumber,
          date: new Date(currentDate),
          recurringAmount,
          status: paymentNumber === 1 ? 'upcoming' : 'scheduled'
        });

        if (i < noOfInstallments - 1) { // Don't increment date after last payment
          currentDate = getNextDate(currentDate, frequency);
        }
        paymentNumber++;
      }
    } else {
      // For subscription plans
      const end = formData.finalPaymentDate ? new Date(formData.finalPaymentDate) : null;
      while ((!end || currentDate <= end) && paymentNumber <= 12) {
        schedule.push({
          paymentNumber,
          date: new Date(currentDate),
          recurringAmount,
          status: paymentNumber === 1 ? 'upcoming' : 'scheduled'
        });

        currentDate = getNextDate(currentDate, frequency);
        paymentNumber++;
      }
    }

    return schedule;
  };
  const schedule = generateSchedule();
  const totalAmount = schedule.reduce((sum, payment) => sum + payment.recurringAmount, 0);

  // Validation logic for different plan types
  let validationInfo = null;
  if (planType === 'BAL' && formData.totalBalance) {
    const expectedTotal = formData.totalBalance || 0;
    const isValid = Math.abs(totalAmount - expectedTotal) < 0.01;
    validationInfo = {
      isValid,
      message: `Balance Due: $${expectedTotal.toFixed(2)} | Total Installments: ${formData.noOfInstallments}`
    };
  } else if (planType === 'pretreatment' && formData.totalTreatmentCost) {
    const expectedTotal = (formData.totalTreatmentCost || 0) - (formData.downPayment || 0);
    const isValid = Math.abs(totalAmount - expectedTotal) < 0.01; // Allow for rounding differences
    validationInfo = {
      isValid,
      message: `Treatment Cost: $${formData.totalTreatmentCost.toFixed(2)} | Down Payment: $${(formData.downPayment || 0).toFixed(2)} | Installments: $${totalAmount.toFixed(2)}`
    };
  }



  if (schedule.length === 0) {
    return (
      <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
        <div className="text-center text-gray-500">
          <LuCalendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p>Complete the form to see payment schedule preview</p>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <LuCalendar className="w-5 h-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Payment Schedule Preview</h4>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Payments</div>
          <div className="text-lg font-semibold text-gray-900">${totalAmount.toFixed(2)}</div>
        </div>
      </div>
      {/* Validation info for BAL-based and pre-treatment plans */}
      {validationInfo && (
        <div className={`mb-4 p-3 rounded-lg border ${validationInfo.isValid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
          }`}>
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${validationInfo.isValid ? 'bg-green-500' : 'bg-amber-500'
              }`} />
            <span className={`text-sm font-medium ${validationInfo.isValid ? 'text-green-800' : 'text-amber-800'
              }`}>
              {validationInfo.message}
            </span>
          </div>
          {!validationInfo.isValid && (
            <p className="mt-1 text-xs text-amber-700">
              {planType === 'BAL'
                ? 'Total installments do not match BAL due. Please review the installment configuration.'
                : 'Payment amounts do not match the expected total. Please review the installment configuration.'
              }
            </p>
          )}
        </div>
      )}


      {/* Schedule table */}
      <div className="overflow-hidden">
        <div className="overflow-y-auto max-h-64">
          <table className="min-w-full">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Payment #</th>
                <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Due Date</th>

                <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-2 text-xs font-medium text-center text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {schedule.map((payment) => (
                <tr key={payment.paymentNumber} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">
                    #{payment.paymentNumber}
                  </td>

                  <td className="px-4 py-2 text-sm text-gray-700">
                    {payment.date.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>


                  <td className="px-4 py-2 text-sm font-medium text-right text-gray-900">
                    ${payment.recurringAmount.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${payment.status === 'upcoming'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-700'
                      }`}>
                      {payment.status === 'upcoming' ? (
                        <>
                          <LuClock className="w-3 h-3 mr-1" />
                          Next
                        </>
                      ) : (
                        'Scheduled'
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {schedule.length >= 12 && (planType === 'SUB' && !formData.finalPaymentDate) && (
          <div className="mt-2 text-center">
            <span className="text-xs text-gray-500">
              Showing first 12 payments • Subscription continues indefinitely
            </span>
          </div>
        )}

        {schedule.length >= 12 && (planType === 'SUB' && formData.finalPaymentDate) && (
          <div className="mt-2 text-center">
            <span className="text-xs text-gray-500">
              Showing first 12 payments • Additional payments will continue until end date
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentSchedulePreview